import { Outlet, useMatch } from 'react-router-dom'
import { MessagesSquare } from 'lucide-react'
import MessagesView from './MessagesView'

/**
 * The threads down one side, the conversation on the other (D-702).
 *
 * <b>`asked`</b>: *"i'm not sure i love having one page be all the threads,
 * clicking into them, then having to click back to all threads, would it be
 * better to run it more like discord or msft teams with threads on left and
 * comms on right?"*
 *
 * <b>One screen at a phone width, two from a tablet up</b> — the master-detail
 * shape, which is what both of those apps actually do once the window is narrow
 * enough. The panes are the same two components either way; what changes is
 * which of them is on screen.
 *
 * <b>The routes do not change</b> (`/messages`, `/messages/new`,
 * `/messages/:id`), so every link, notification and bookmark that points at a
 * conversation still opens it. They are nested under a layout route instead of
 * sitting flat, and that nesting is the whole mechanism: the list is rendered
 * by the parent, so it survives the child changing.
 *
 * <b>Sticky, with a bounded scroll box.</b> The alternative is
 * `h-[calc(100vh-Xrem)]` on both panes, where X is the height of a header this
 * component cannot see and which changes the day anybody adds a row to the
 * rail. This is not that: the column is `sticky top-4`, so it pins itself 1rem
 * below the viewport top wherever the header happens to end, and the only
 * figure it needs is its own offset. `calc(100dvh-2rem)` is that offset plus
 * 1rem of breathing room at the foot — nothing sits below the pane from `md`
 * up, because the bottom bar is `sm:hidden`.
 *
 * <b>An earlier version of this comment claimed it asked for no number at all,
 * one line above a hard-coded `9rem`.</b> That number reserved space nothing
 * occupied: `observed` at 1280×420, the box clipped its own list to 276px with
 * **128px of empty viewport below it**. Found by an audit reading the sentence
 * against the line under it, and then measured.
 *
 * <b>Literals, not content blocks</b> (D-678): this is inside the signed-in
 * application, so the words are ours. `MessagesView` beside it is older and
 * keeps its blocks — churning those back is the busywork D-678 exists to
 * prevent.
 */
export default function MessagesLayout() {
  /*
   * <b>Asked of the router, not of the string.</b> This was a pair of
   * hand-written comparisons against `/messages` and `/messages/`, and routes
   * are matched case-insensitively — so `/Messages`, which React Router happily
   * serves, read as *a conversation is open*: on a phone that is the empty
   * right-hand pane with no list, no back link and nothing to tap. `useMatch`
   * uses the same matching the route itself was resolved by, handles the
   * trailing slash, and is shorter than what it replaced. Named by an audit
   * that drove the casing through a real router rather than reasoning about it.
   */
  const atTheList = useMatch('/messages')
  const detailOpen = atTheList === null

  return (
    <div className="flex grow flex-col gap-4 md:flex-row md:items-start md:gap-5">
      {/*
        <b>Hidden on a phone, not merely sitting behind the detail.</b>
        Rendering both would stack two screens on top of each other, which is
        the whole reason the phone keeps one pane. From `md` up both are on
        screen, which is the point of the change.

        <b>`nav`, not `aside`.</b> An `aside` is `role="complementary"` —
        content *tangentially* related to the rest — and this list is the
        screen's primary way of getting anywhere, which is how Teams and Gmail
        expose theirs. A screen-reader user jumping by landmark looks for a
        navigation.
      */}
      <nav
        aria-label="Conversations"
        className={`${detailOpen ? 'hidden md:block' : 'block'} w-full shrink-0 md:sticky md:top-4 md:max-h-[calc(100dvh-2rem)] md:w-[18.5rem] md:overflow-y-auto lg:w-[21rem]`}
      >
        <MessagesView />
      </nav>

      <section className={`${detailOpen ? 'block' : 'hidden md:block'} min-w-0 grow`}>
        <Outlet />
      </section>
    </div>
  )
}

/**
 * What the right-hand pane holds before she has picked anything (D-702).
 *
 * <b>Only ever seen from a tablet up.</b> At a phone width `/messages` is the
 * list and this pane is not rendered at all, so this is never the whole screen
 * — which is why it is a quiet line rather than an empty state with a button.
 * The thing to do next is already on the left.
 */
export function NoConversationPicked() {
  return (
    <div className="flex min-h-[18rem] flex-col items-center justify-center gap-3 rounded-2xl border border-line bg-surface px-6 py-10 text-center">
      <MessagesSquare size={28} className="text-ink-3" aria-hidden="true" />
      <p className="text-[13px] text-ink-2">Pick a conversation to read it.</p>
    </div>
  )
}
